// Phase 4.3 (hh-4ef) — VideosScreen.
//
// Ported from designproject/files/screens-notifs.jsx (`VideosScreen`). case-27:
// подборка коротких роликов; после ролика — явная связь с пользовательским
// оборудованием. Список грузится из GET /api/v1/videos; бэкенд проставляет
// hasMatchingEquipment / matchingEquipmentId, поэтому «у тебя такой есть»
// показывается по реальному оборудованию пользователя.
//
// hh-cmv: экран ролика проигрывает video.url в настоящем <video controls>.
// Сид роликов сейчас указывает на ещё не поднятый хостинг — поэтому у плеера
// есть graceful-фолбэк onError: вместо сломанного <video> показывается
// заглушка-превью с пометкой «ролик пока недоступен».

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { T, Icon, Btn } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { videos, humanizeError } from '../../api';
import type { VideoRow } from '../../api/endpoints/videos.js';
import { formatLength } from '../../api/endpoints/videos.js';

export interface VideosScreenProps {
  readonly router: RouterApi;
}

type Status = 'loading' | 'ready' | 'error';

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

export function VideosScreen({ router }: VideosScreenProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [list, setList] = useState<readonly VideoRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    videos
      .listVideos()
      .then((res) => {
        if (cancelled) return;
        setList(res.videos);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const open = openId !== null ? list.find((v) => v.id === openId) ?? null : null;

  if (open !== null) {
    return (
      <VideoDetail
        video={open}
        onBack={() => setOpenId(null)}
        onOpenEquipment={(id) => router.go('eq_card', { id })}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title="Обучение"
        subtitle="Короткие ролики — зачем что-то делать"
        onBack={() => router.back()}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '14px 2px' }}>Загружаю…</div>
        )}
        {status === 'error' && error !== null && <ErrorBanner message={error} />}
        {status === 'ready' && list.length === 0 && (
          <EmptyState text="Роликов пока нет." />
        )}
        {status === 'ready' &&
          list.map((v) => (
            <div
              key={v.id}
              onClick={() => setOpenId(v.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: 14,
                marginTop: 10,
                cursor: 'pointer',
              }}
            >
              <Thumb size="sm" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                  {v.title}
                </div>
                <div style={{ fontSize: 12, color: T.ink3, marginTop: 3 }}>
                  {formatLength(v.lengthSec)}
                  {v.hasMatchingEquipment && ' · связан с твоим оборудованием'}
                </div>
              </div>
              <Icon name="chevR" size={16} color={T.ink3} />
            </div>
          ))}
      </div>
    </div>
  );
}

interface VideoDetailProps {
  readonly video: VideoRow;
  readonly onBack: () => void;
  readonly onOpenEquipment: (id: string) => void;
}

function VideoDetail({ video, onBack, onOpenEquipment }: VideoDetailProps): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader title="Ролик" onBack={onBack} />
      <div style={{ flex: 1, padding: '4px 16px 24px', overflow: 'auto' }}>
        <VideoPlayer url={video.url} length={formatLength(video.lengthSec)} />
        <div style={{ fontSize: 19, fontWeight: 700, color: T.ink, marginTop: 16 }}>{video.title}</div>
        <div style={{ fontSize: 13, color: T.ink3, marginTop: 4 }}>
          Короткий ролик о том, зачем это нужно
        </div>

        {video.hasMatchingEquipment && video.matchingEquipmentId !== null && (
          <div
            style={{
              marginTop: 16,
              padding: 16,
              background: T.brandSoft,
              border: `1px solid ${T.brandSoft2}`,
              borderRadius: 14,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: T.brandDeep,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              У тебя как раз такое есть
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6 }}>
              Открой карточку своего оборудования — там регламент и сроки ТО.
            </div>
            <Btn
              kind="primary"
              iconAfter="chevR"
              style={{ width: '100%', marginTop: 10 }}
              onClick={() => onOpenEquipment(video.matchingEquipmentId as string)}
            >
              Открыть карточку
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Проигрыватель ролика. Рендерит настоящий <video controls> с video.url.
 * Сид роликов сейчас указывает на ещё не развёрнутый хостинг — при сбое
 * загрузки (onError) плеер переключается на заглушку-превью, чтобы экран не
 * показывал сломанный элемент (hh-cmv).
 */
function VideoPlayer({ url, length }: { readonly url: string; readonly length: string }): JSX.Element {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return <Thumb size="lg" length={length} note="Ролик пока недоступен" />;
  }

  return (
    <video
      src={url}
      controls
      playsInline
      preload="metadata"
      onError={() => setFailed(true)}
      style={{
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: 16,
        background: T.ink,
        display: 'block',
      }}
    />
  );
}

interface ThumbProps {
  readonly size: 'sm' | 'lg';
  readonly length?: string;
  /** Пояснение поверх превью — заменяет иконку play (фолбэк недоступного ролика). */
  readonly note?: string;
}

function Thumb({ size, length, note }: ThumbProps): JSX.Element {
  const lg = size === 'lg';
  return (
    <div
      style={{
        ...(lg
          ? { aspectRatio: '16 / 9', borderRadius: 16 }
          : { width: 64, height: 48, borderRadius: 10 }),
        background: T.ink,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 10px, transparent 10px 20px)',
        }}
      />
      {note !== undefined ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            padding: '0 16px',
            textAlign: 'center',
          }}
        >
          <Icon name="play" size={lg ? 26 : 18} color="rgba(255,255,255,0.45)" />
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: lg ? 13 : 11, fontWeight: 600 }}>
            {note}
          </div>
        </div>
      ) : (
        <Icon name="play" size={lg ? 28 : 18} color="#fff" />
      )}
      {lg && length !== undefined && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {length}
        </div>
      )}
    </div>
  );
}

function EmptyState({ text }: { readonly text: string }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 14,
        padding: '20px 14px',
        background: T.surfaceAlt,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        fontSize: 13,
        color: T.ink3,
        textAlign: 'center',
        fontWeight: 500,
      }}
    >
      {text}
    </div>
  );
}

function ErrorBanner({ message }: { readonly message: string }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 12px',
        background: T.dangerSoft,
        border: '1px solid #E9C7BF',
        borderRadius: 12,
        fontSize: 12.5,
        color: T.danger,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}
